/**
 * Types pour le module Prévisionnel de Trésorerie
 *
 * Ce module gère TOUS les flux financiers :
 * - Encaissements : factures clients, paiements, avenants
 * - Décaissements : frais, fournisseurs, contrats ST, heures sup, salaires, remises
 */

// Catégories d'encaissements
export type CategorieEncaissement =
  | 'facture_client'      // Facture client à encaisser
  | 'paiement_attendu'    // Paiement en attente
  | 'avenant';            // Avenant (+)

// Catégories de décaissements
export type CategorieDecaissement =
  | 'frais_materiaux'     // Frais chantier type Matériaux
  | 'frais_sous_traitant' // Frais chantier type Sous-traitant
  | 'frais_annexes'       // Autres frais (assurance, transport, etc.)
  | 'facture_fournisseur' // Factures fournisseurs
  | 'contrat_st'          // Reste à payer sur contrat ST
  | 'heures_sup'          // Heures supplémentaires
  | 'salaires'            // Salaires équipe
  | 'remise'              // Remise accordée (diminue entrées = sortie)
  | 'charges_fixes';      // Charges fixes récurrentes (loyer, assurances, etc.)

// Union de toutes les catégories
export type CategorieFlux = CategorieEncaissement | CategorieDecaissement;

// Mode d'affichage HT/TTC pour le toggle trésorerie
export type ModeAffichageTresorerie = 'ht' | 'ttc';

/**
 * Un flux de trésorerie (entrée ou sortie d'argent)
 */
export interface FluxTresorerie {
  id: string;
  type: 'encaissement' | 'decaissement';
  categorie: CategorieFlux;
  libelle: string;
  montant: number;           // Toujours positif (TTC), le type indique le sens
  montant_ht: number;        // Montant HT correspondant
  date_prevue: string;       // Date prévue du flux (format YYYY-MM-DD)
  date_reelle?: string;      // Date réelle si déjà réalisé
  statut: 'prevu' | 'en_retard' | 'realise';
  reference_id?: string;     // ID de la source (facture, frais, etc.)
  reference_table?: string;  // Nom de la table source
  chantier_id?: string;
  chantier_nom?: string;
}

/**
 * Données de trésorerie pour un mois
 */
export interface MoisTresorerie {
  mois: string;              // "2026-02"
  label: string;             // "Février 2026"
  solde_debut: number;
  encaissements: FluxTresorerie[];
  decaissements: FluxTresorerie[];
  total_encaissements: number;
  total_decaissements: number;
  solde_fin: number;
  // Détail par catégorie pour analyse
  detail_encaissements: Partial<Record<CategorieEncaissement, number>>;
  detail_decaissements: Partial<Record<CategorieDecaissement, number>>;
  // Versions HT pour le switch HT/TTC
  solde_debut_ht: number;
  total_encaissements_ht: number;
  total_decaissements_ht: number;
  solde_fin_ht: number;
  detail_encaissements_ht: Partial<Record<CategorieEncaissement, number>>;
  detail_decaissements_ht: Partial<Record<CategorieDecaissement, number>>;
}

/**
 * Prévision de trésorerie complète (3 mois glissants)
 */
export interface PrevisionTresorerie {
  solde_actuel: number;
  solde_actuel_source: 'manuel' | 'calcule';
  date_calcul: string;
  mois: MoisTresorerie[];
  // Totaux globaux
  total_encaissements_3mois: number;
  total_decaissements_3mois: number;
  solde_fin_periode: number;
  // Versions HT
  solde_actuel_ht: number;
  total_encaissements_3mois_ht: number;
  total_decaissements_3mois_ht: number;
  solde_fin_periode_ht: number;
  // Alertes
  mois_en_deficit: string[];  // Liste des mois avec solde < 0
}

/**
 * Paramètres utilisateur pour la trésorerie
 */
export interface ParametresTresorerie {
  solde_initial_manuel: number | null;
  utiliser_solde_manuel: boolean;
}

/**
 * Données retournées par les fonctions SQL
 */
export interface FluxSQL {
  id: string;
  categorie: string;
  libelle: string;
  montant: number;
  montant_ht: number;
  date_prevue: string;
  statut: string;
  reference_id: string | null;
  reference_table: string | null;
  chantier_id: string | null;
  chantier_nom: string | null;
}

export interface SalairesMoisSQL {
  mois: string;
  montant_salaires: number;
  nb_membres: number;
  detail_membres: {
    membre_id: string;
    nom: string;
    prenom: string;
    jours: number;
    cout: number;
  }[];
}

/**
 * Labels pour l'affichage des catégories
 */
export const CATEGORIES_LABELS: Record<CategorieFlux, string> = {
  // Encaissements
  facture_client: 'Facture client',
  paiement_attendu: 'Paiement attendu',
  avenant: 'Avenant',
  // Décaissements
  frais_materiaux: 'Matériaux',
  frais_sous_traitant: 'Sous-traitant',
  frais_annexes: 'Frais annexes',
  facture_fournisseur: 'Fournisseur',
  contrat_st: 'Contrat ST',
  heures_sup: 'Heures sup',
  salaires: 'Salaires',
  remise: 'Remise',
  charges_fixes: 'Charges fixes',
};

/**
 * Couleurs pour l'affichage des catégories
 */
export const CATEGORIES_COLORS: Record<CategorieFlux, string> = {
  // Encaissements (verts)
  facture_client: '#22c55e',
  paiement_attendu: '#4ade80',
  avenant: '#86efac',
  // Décaissements (rouges/oranges)
  frais_materiaux: '#f97316',
  frais_sous_traitant: '#8b5cf6',
  frais_annexes: '#6b7280',
  facture_fournisseur: '#ef4444',
  contrat_st: '#a855f7',
  heures_sup: '#ec4899',
  salaires: '#3b82f6',
  remise: '#fbbf24',
  charges_fixes: '#94a3b8',
};
